import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useRef } from 'react';
import {
  Navigate,
  NavigationType,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { company } from '~/company/Company';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { useScrollRestoration } from '~/hooks/useScrollRestoration';
import { useStores } from '~/hooks/useStore';
import {
  AboutDeRoute,
  AboutElectroniumRoute,
  AboutLocaleeRoute,
  AddressRoute,
  BrandRoute,
  CabinetRoute,
  CareersRoute,
  CartRoute,
  CatalogRoute,
  CheckoutRoute,
  ContactsRoute,
  ContactUsCeriseRoute,
  CustomerCareRoute,
  DeliveryDeRoute,
  EventsRoute,
  FaqDeRoute,
  FaqElectroniumRoute,
  FaqRoute,
  FavsRoute,
  InfluencersRoute,
  LegalsRoute,
  LoyaltyRoute,
  OrderPaymentRoute,
  OrderRoute,
  OrdersRoute,
  OurStoryRoute,
  PaymentElectroniumRoute,
  PaymentMethodRoute,
  PersonalDataRoute,
  ProductRoute,
  PromocodesRoute,
  ReferralRoute,
  RefundRoute,
  ReturnsDeRoute,
  SearchRoute,
  ShippingReturnRoute,
  ShopRoute,
  SmiDeRoute,
  SubscriptionRoute,
} from '~/routes/manifest';
import { RouteErrorBoundary } from '~/routes/RouteErrorBoundary';
import { RouteFallback } from '~/routes/RouteFallback';
import resetScroll from '~/utils/resetScroll';

import AuthorizationGuard from '../../components/AuthorizationGuard/AuthorizationGuard';
import { useGlobal } from '../../hooks/useGlobal';
import Icons from '../Icons/Icons';
import MainV3 from '../MainV3/MainV3';
import NotFound from '../NotFound/NotFound';
import { ROUTES } from './constants';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';

export const NOT_FOUND_PAGE = '/not-found';

export default observer(() => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const layoutRef = useRef<HTMLDivElement>(null);
  const { shopCategories, homeCategories, heroBanner } = useStores();

  const LayoutComponent = isMobile ? MobileLayout : DesktopLayout;

  useScrollRestoration({ isMobile });

  // Only a change of pathname resets the scroll. `navigationType` is read from
  // the render that changed it and deliberately kept out of the dependencies:
  // the catalog replaces its query string right after a pop lands, which flips
  // the type to REPLACE without moving the reader anywhere, and re-running here
  // would throw them back to the top of the list they just returned to.
  useEffect(() => {
    if (layoutRef?.current) {
      resetScroll({
        isMobile,
        isHistoryPop: navigationType === NavigationType.Pop,
      });
    }
  }, [pathname, layoutRef]);

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !shopCategories.list.length) {
        shopCategories.request.run(code);
      }
    },
    [shopCategories],
  );

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !homeCategories.list.length) {
        homeCategories.paginationRequest.loadPage(1, code);
        if (pathname === ROUTES.index) {
          resetScroll({ isMobile });
        }
      }
    },
    [homeCategories],
  );

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !heroBanner.isInitialized) {
        heroBanner.load(code);
      }
    },
    [heroBanner],
  );

  return (
    <>
      <LayoutComponent layoutRef={layoutRef}>
        <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path={ROUTES.index} element={<MainV3 />} />
              <Route path={ROUTES.catalog} element={<CatalogRoute />} />
              <Route path={ROUTES.shop} element={<ShopRoute />} />
              <Route path={ROUTES.product} element={<ProductRoute />} />
              {/* TODO: It should be in /cabinet scope. */}
              {!company.hideCabinetRoutes && (
                <>
                  <Route path={ROUTES.favs} element={<FavsRoute />} />
                  <Route path={ROUTES.cart} element={<CartRoute />} />
                  <Route
                    path={ROUTES.checkout}
                    element={
                      <AuthorizationGuard>
                        <CheckoutRoute />
                      </AuthorizationGuard>
                    }
                  />
                  <Route
                    path={ROUTES.order}
                    element={
                      <AuthorizationGuard>
                        <OrderRoute />
                      </AuthorizationGuard>
                    }
                  />
                  {company.isMobileMoneyPaymentEnabled && (
                    <Route
                      path={ROUTES.order_payment}
                      element={
                        <AuthorizationGuard>
                          <OrderPaymentRoute />
                        </AuthorizationGuard>
                      }
                    />
                  )}
                  <Route
                    path={ROUTES.loyalty}
                    element={
                      <AuthorizationGuard requireVerified>
                        <LoyaltyRoute />
                      </AuthorizationGuard>
                    }
                  />
                  <Route path={ROUTES.cabinet}>
                    <Route
                      index
                      element={
                        <AuthorizationGuard>
                          <CabinetRoute />
                        </AuthorizationGuard>
                      }
                    />
                    <Route path={ROUTES.cabinet_orders}>
                      <Route
                        index
                        element={
                          <AuthorizationGuard>
                            <OrdersRoute />
                          </AuthorizationGuard>
                        }
                      />
                      <Route
                        path={ROUTES.cabinet_orders_tab}
                        element={
                          <AuthorizationGuard>
                            <OrdersRoute />
                          </AuthorizationGuard>
                        }
                      />
                    </Route>
                    <Route
                      path={ROUTES.cabinet_personalData}
                      element={
                        <AuthorizationGuard>
                          <PersonalDataRoute />
                        </AuthorizationGuard>
                      }
                    />
                    <Route
                      path={ROUTES.cabinet_address}
                      element={
                        <AuthorizationGuard>
                          <AddressRoute />
                        </AuthorizationGuard>
                      }
                    />
                    <Route
                      path={ROUTES.cabinet_events}
                      element={
                        <AuthorizationGuard>
                          <EventsRoute />
                        </AuthorizationGuard>
                      }
                    />
                    <Route
                      path={ROUTES.cabinet_subscriptions}
                      element={
                        <AuthorizationGuard>
                          <SubscriptionRoute />
                        </AuthorizationGuard>
                      }
                    />
                    {company.showSavedPaymentCards && (
                      <Route
                        path={ROUTES.cabinet_paymentMethod}
                        element={
                          <AuthorizationGuard>
                            <PaymentMethodRoute />
                          </AuthorizationGuard>
                        }
                      />
                    )}
                    {company.showReferralProgram && (
                      <Route
                        path={ROUTES.cabinet_referral}
                        element={
                          <AuthorizationGuard requireVerified>
                            <ReferralRoute />
                          </AuthorizationGuard>
                        }
                      />
                    )}
                    {company.showReferralProgram && (
                      <Route
                        path={ROUTES.cabinet_referral_rewards}
                        element={
                          <Navigate to="/cabinet/promocodes/referral" replace />
                        }
                      />
                    )}
                    {company.showReferralProgram && (
                      <Route path={ROUTES.cabinet_promocodes}>
                        <Route
                          index
                          element={
                            <AuthorizationGuard>
                              <PromocodesRoute />
                            </AuthorizationGuard>
                          }
                        />
                        <Route
                          path={ROUTES.cabinet_promocodes_tab}
                          element={
                            <AuthorizationGuard>
                              <PromocodesRoute />
                            </AuthorizationGuard>
                          }
                        />
                      </Route>
                    )}
                  </Route>
                </>
              )}
              <Route path={ROUTES.search} element={<SearchRoute />} />
              <Route path={ROUTES.legals} element={<LegalsRoute />} />
              <Route
                path={`${ROUTES.legals}/:legalSlug`}
                element={<LegalsRoute />}
              />

              {company.match({
                dubaiexpress: (
                  <>
                    <Route path={ROUTES.about} element={<AboutDeRoute />} />
                    <Route path={ROUTES.contacts} element={<ContactsRoute />} />
                    <Route path={ROUTES.faq} element={<FaqDeRoute />} />
                    <Route
                      path={ROUTES.delivery}
                      element={<DeliveryDeRoute />}
                    />
                    <Route path={ROUTES.returns} element={<ReturnsDeRoute />} />
                    <Route path={ROUTES.smi} element={<SmiDeRoute />} />
                  </>
                ),
                dubaiexpresscom: (
                  <>
                    <Route path={ROUTES.about} element={<AboutDeRoute />} />
                    <Route path={ROUTES.contacts} element={<ContactsRoute />} />
                    <Route path={ROUTES.faq} element={<FaqDeRoute />} />
                    <Route
                      path={ROUTES.delivery}
                      element={<DeliveryDeRoute />}
                    />
                    <Route path={ROUTES.returns} element={<ReturnsDeRoute />} />
                    <Route path={ROUTES.smi} element={<SmiDeRoute />} />
                  </>
                ),
                citydrinks: (
                  <>
                    <Route
                      path={ROUTES.shippingReturn}
                      element={<ShippingReturnRoute />}
                    />
                    <Route path={ROUTES.refund} element={<RefundRoute />} />
                    <Route path={ROUTES.faq} element={<FaqRoute />} />
                    <Route
                      path={ROUTES.influencers}
                      element={<InfluencersRoute />}
                    />
                    <Route path={ROUTES.careers} element={<CareersRoute />} />
                    <Route
                      path={ROUTES.contactUs}
                      element={<Navigate to="/" replace />}
                    />
                    <Route path={ROUTES.brand} element={<BrandRoute />} />
                    <Route path={ROUTES.ourStory} element={<OurStoryRoute />} />
                  </>
                ),
                electronium: (
                  <>
                    <Route
                      path={ROUTES.faq}
                      element={<FaqElectroniumRoute />}
                    />
                    <Route
                      path={ROUTES.customerCare}
                      element={<CustomerCareRoute />}
                    />
                    <Route
                      path={ROUTES.about}
                      element={<AboutElectroniumRoute />}
                    />
                    <Route
                      path={ROUTES.payment}
                      element={<PaymentElectroniumRoute />}
                    />
                  </>
                ),
                mcosmetic: (
                  <Route path={ROUTES.contacts} element={<ContactsRoute />} />
                ),
                cerise: (
                  <Route
                    path={ROUTES.contactUs}
                    element={<ContactUsCeriseRoute />}
                  />
                ),
                localee: (
                  <Route path={ROUTES.about} element={<AboutLocaleeRoute />} />
                ),
              })}
              {import.meta.env.DEV && (
                <Route path="/icons" element={<Icons />} />
              )}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </LayoutComponent>
    </>
  );
});
